import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";
import { Panel } from "../../components/ui/Panel";

/**
 * Logos and banners for signatures and canned responses.
 *
 * These are the only stored objects served without a session, and they have to
 * be: a logo behind the session guard is a broken image in the customer's
 * inbox. Each row gives a ready-made <img> tag to paste, because getting the
 * URL right by hand is the step people get wrong.
 */
export function ImageSettings() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const images = useQuery({
    queryKey: queryKeys.brandImages,
    queryFn: api.brandImages,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.brandImages });

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadBrandImage(file),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Upload failed"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteBrandImage(id),
    onSuccess: invalidate,
  });

  return (
    <Panel
      collapsible
      title="Images"
    >
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) upload.mutate(file);
          event.target.value = "";
        }}
      />
      <Button
        className="mb-3"
        onClick={() => fileInput.current?.click()}
        disabled={upload.isPending}
      >
        {upload.isPending ? "Uploading…" : "Upload an image"}
      </Button>

      {error ? <p className="mb-3 text-xs text-red-600">{error}</p> : null}

      {(images.data ?? []).length === 0 ? (
        <p className="text-sm text-muted">Nothing uploaded yet.</p>
      ) : (
        <ul className="space-y-2">
          {(images.data ?? []).map((image) => {
            const url = `${window.location.origin}/api/public/images/${image.id}`;
            return (
              <li
                key={image.id}
                className="flex items-center gap-3 rounded-md border border-line p-2"
              >
                <img
                  src={url}
                  alt=""
                  className="h-10 w-16 shrink-0 rounded bg-surface-2 object-contain"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-content">{image.name}</div>
                  <div className="text-[11px] text-faint">
                    {Math.max(1, Math.round(image.sizeBytes / 1024))} KB
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void navigator.clipboard.writeText(
                      `<img src="${url}" alt="${image.name}" style="max-width:100%">`,
                    );
                  }}
                >
                  Copy tag
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove "${image.name}"? Mail already sent keeps working — this only stops it being offered again.`,
                      )
                    ) {
                      remove.mutate(image.id);
                    }
                  }}
                >
                  Remove
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
